import { ReactElement } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormGroup, PasswordController, Slider, TextFieldController } from 'components';
import { FIELDS_VALUE_LENGTH } from 'constants/fieldsLength';
import { pillFieldSx } from 'features/auth/containers/SignInForm/styles';
import { SignUpButton } from 'features/signUp/components/SignUpForm/styled';
import { useWindowSize } from 'hooks';
import { ApiErrors, CompanySize } from 'types';
import { passwordRules } from 'validations';

import { TermsAndConditions } from '../TermsAndConditions';
import { RegisterCompanyFormValues, useRegisterCompanyForm } from './hooks';
import {
  companyNameRules,
  companyNumberRules,
  emailFieldRules,
  firstNameRules,
  industryRules,
  lastNameRules,
} from './rules';
import { InputW, SliderLabel, WranDescription } from './styled';

interface RegisterCompanyFormProps {
  defaultValues: RegisterCompanyFormValues;
  handleRegisterSubmit: (data: RegisterCompanyFormValues) => void;
  errors?: Record<string, ApiErrors['errors']> | null;
}

export function RegisterCompanyForm({
  defaultValues,
  handleRegisterSubmit,
  errors,
}: RegisterCompanyFormProps): ReactElement {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const { handleFormSubmit, control, passwordHide, emailValidationMutation } =
    useRegisterCompanyForm({ defaultValues, handleRegisterSubmit, errors });
  const formGroupMarginBottom = 24;

  const companySizeValues = Object.values(CompanySize);
  const companySizeValuesCount = companySizeValues.length - 1;

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormGroup row marginBottom={formGroupMarginBottom}>
        <InputW>
          <TextFieldController
            control={control}
            name="firstName"
            id="firstName"
            label={t('companyRegistration.personalDataForm.firstName')}
            placeholder={t('companyRegistration.personalDataForm.firstName')}
            rules={firstNameRules}
            maxLength={FIELDS_VALUE_LENGTH.firstName.max}
            sx={pillFieldSx}
          />
        </InputW>
        {!isMobile && (
          <InputW>
            <TextFieldController
              name="lastName"
              id="lastName"
              control={control}
              label={t('companyRegistration.personalDataForm.lastName')}
              placeholder={t('companyRegistration.personalDataForm.lastName')}
              rules={lastNameRules}
              maxLength={FIELDS_VALUE_LENGTH.lastName.max}
              sx={pillFieldSx}
            />
          </InputW>
        )}
      </FormGroup>
      {isMobile && (
        <FormGroup marginBottom={formGroupMarginBottom}>
          <InputW>
            <TextFieldController
              name="lastName"
              id="lastName"
              control={control}
              label={t('companyRegistration.personalDataForm.lastName')}
              placeholder={t('companyRegistration.personalDataForm.lastName')}
              rules={lastNameRules}
              maxLength={FIELDS_VALUE_LENGTH.lastName.max}
              sx={pillFieldSx}
            />
          </InputW>
        </FormGroup>
      )}
      <FormGroup marginBottom={formGroupMarginBottom}>
        <TextFieldController
          name="email"
          id="email"
          control={control}
          type="email"
          emailTrigger={true}
          checkEmailExsist={emailValidationMutation}
          label={t('companyRegistration.personalDataForm.email')}
          placeholder={t('companyRegistration.personalDataForm.email')}
          autoComplete="new-password"
          rules={emailFieldRules}
          maxLength={254}
          sx={pillFieldSx}
        />
      </FormGroup>
      <FormGroup marginBottom={24} row>
        {passwordHide && (
          <PasswordController
            className="personalData"
            name="password"
            id="password"
            control={control}
            label={t('companyRegistration.personalDataForm.password')}
            placeholder={t('companyRegistration.personalDataForm.password')}
            autoComplete="new-password"
            rules={passwordRules}
            maxLength={60}
            sx={pillFieldSx}
            withAdornment
          />
        )}
        {!passwordHide && (
          <WranDescription>{t('companyRegistration.warnMessage.title')}</WranDescription>
        )}
      </FormGroup>
      <FormGroup marginBottom={24}>
        <TextFieldController
          name="companyName"
          control={control}
          label={t('companyRegistration.companyProfileForm.companyName')}
          placeholder={t('companyRegistration.companyProfileForm.companyName')}
          rules={companyNameRules}
          sx={pillFieldSx}
          maxLength={40}
        />
      </FormGroup>
      <FormGroup marginBottom={24}>
        <TextFieldController
          name="number"
          control={control}
          label={t('companyRegistration.companyProfileForm.number')}
          placeholder={t('companyRegistration.companyProfileForm.number')}
          rules={companyNumberRules}
          sx={pillFieldSx}
          maxLength={20}
        />
      </FormGroup>
      <FormGroup marginBottom={24}>
        <TextFieldController
          name="industry"
          control={control}
          label={t('companyRegistration.companyProfileForm.industry')}
          placeholder={t('companyRegistration.companyProfileForm.industry')}
          rules={industryRules}
          sx={pillFieldSx}
          maxLength={70}
        />
      </FormGroup>
      <FormGroup marginBottom={24}>
        <Controller
          control={control}
          name="companySize"
          render={({ field: { value, onChange } }) => (
            <Slider
              min={0}
              max={companySizeValuesCount}
              step={1}
              onChange={onChange}
              value={value}
              valueLabelFormat={(x) => `${companySizeValues[x]}`}
              aria-label="company-size"
            />
          )}
        />
        <SliderLabel>{t('companyRegistration.companyProfileForm.howMany')}</SliderLabel>
      </FormGroup>
      <FormGroup marginBottom={24}>
        <TermsAndConditions />
      </FormGroup>
      <FormGroup marginBottom={8}>
        <SignUpButton type="submit" variant="contained" color="info">
          {t('signUp.createAccount')}
        </SignUpButton>
      </FormGroup>
    </Form>
  );
}
